// 快船 App 根组件
// 串联 AppShell（三 Tab）+ 信令（useSignaling）+ WebRTC（useWebRTCPeer）+ 加入房间流程
//
// 关键接线：
//   signaling.onOffer  → webrtc.receiveFrom   （接收方：收到发送方连接请求）
//   signaling.onAnswer → webrtc.receiveAnswer （发送方：收到接收方应答）
//   ReceiveTab.onRoomCreated / JoinRoom.onJoined → signaling.joinRoom

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppShell, type HomeTab } from './components/layout/AppShell';
import { ReceiveTab } from './components/tabs/ReceiveTab';
import { SendTab } from './components/tabs/SendTab';
import { SettingsTab } from './components/tabs/SettingsTab';
import { FriendsPage } from './components/tabs/FriendsPage';
import { ChatPage } from './components/tabs/ChatPage';
import { AuthModal } from './components/auth/AuthModal';
import { AuthGate } from './components/auth/AuthGate';
import { ToastContainer } from './components/common/Toast';
import { useSignaling } from './hooks/useSignaling';
import { useWebRTCPeer } from './hooks/useWebRTCPeer';
import { useSessionStore } from './store/sessionStore';
import { useAuthStore } from './store/authStore';
import { fetchConfig } from './lib/api';
import { loadUnifiedSdk } from './lib/sdk';
import type { SignalingClient } from './lib/signaling/client';
import type { ClientInfo, Friendship } from '@kuaichuan/shared';

// 懒加载加入房间流程（含 html5-qrcode 扫码库，拆出独立 chunk 减小首屏）
const JoinRoom = lazy(() =>
  import('./components/connection/JoinRoom').then((m) => ({ default: m.JoinRoom })),
);

export function App() {
  const [currentTab, setCurrentTab] = useState<HomeTab>('receive');
  const [joinDialog, setJoinDialog] = useState<{ open: boolean; initialCode?: string }>({ open: false });
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);
  // 聊天选中的好友（peerId/peerName）；为 null 时显示好友列表，否则进入聊天页
  const [chatPeer, setChatPeer] = useState<{ id: string; name: string } | null>(null);

  const signaling = useSignaling();
  const webrtc = useWebRTCPeer();
  const sessionStatus = useSessionStore((s) => s.status);
  const isAuthenticated = useAuthStore((s) => !!s.token);
  const navigate = useNavigate();
  const location = useLocation();

  // 保持对最新 signaling client 的引用（供 onOffer 回调内读取，避免闭包陈旧）
  const signalingClientRef = useRef<SignalingClient | null>(null);
  useEffect(() => {
    signalingClientRef.current = signaling.client;
  }, [signaling.client]);

  // 接收方：收到发送方 OFFER → 建立 WebRTC 接收
  const handleOffer = useCallback(
    (peer: ClientInfo, sessionId: string, sdp: string) => {
      const client = signalingClientRef.current;
      if (!client) return;
      // 简单保护：传输进行中时忽略新请求
      if (sessionStatus === 'transferring') return;
      void webrtc.receiveFrom(client, { id: peer.id, alias: peer.alias }, sessionId, sdp);
    },
    [webrtc, sessionStatus],
  );

  // 发送方：收到接收方 ANSWER → 完成 SDP 协商
  const handleAnswer = useCallback(
    (_peer: ClientInfo, _sessionId: string, sdp: string) => {
      webrtc.receiveAnswer(sdp);
    },
    [webrtc],
  );

  // 统一的房间加入逻辑
  const join = useCallback(
    (roomId: string) => {
      setActiveRoomId(roomId);
      void signaling.joinRoom(roomId, { onOffer: handleOffer, onAnswer: handleAnswer });
    },
    [signaling, handleOffer, handleAnswer],
  );

  // URL #join=CODE 自动触发加入流程
  useEffect(() => {
    const hash = location.hash.replace(/^#/, '');
    const m = hash.match(/join=([A-Za-z0-9]{6})/);
    if (m) {
      const code = m[1].toUpperCase();
      setJoinDialog({ open: true, initialCode: code });
      // 清掉 hash，避免刷新重复触发
      navigate(location.pathname, { replace: true });
    } else if (hash.startsWith('room=')) {
      const roomId = hash.slice('room='.length);
      if (roomId) {
        join(roomId);
        navigate(location.pathname, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 启动时拉取配置，加载广告/下载 SDK（APP_ID 来自 worker env）
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await fetchConfig();
        if (cancelled) return;
        await loadUnifiedSdk(cfg.appId, cfg.unifiedAuthBase);
      } catch {
        /* 配置拉取失败不阻塞主功能 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRoomCreated = useCallback(
    (roomId: string) => {
      join(roomId);
    },
    [join],
  );

  const handleJoined = useCallback(
    (roomId: string) => {
      setJoinDialog({ open: false });
      join(roomId);
      setCurrentTab('send');
    },
    [join],
  );

  const openJoinDialog = useCallback(() => {
    setJoinDialog({ open: true });
  }, []);

  const handleLeaveRoom = useCallback(() => {
    signaling.leaveRoom();
    setActiveRoomId(null);
    webrtc.cancelTransfer();
    setCurrentTab('receive');
  }, [signaling, webrtc]);

  // 选中好友进入聊天
  const handleChatWith = useCallback((friend: Friendship) => {
    setChatPeer({ id: friend.friendId, name: friend.friendName });
  }, []);

  // 返回好友列表
  const handleBackToFriends = useCallback(() => {
    setChatPeer(null);
  }, []);

  return (
    <AuthGate>
      <div className="h-full flex flex-col">
        {/* 连接状态条 */}
        <StatusBar
          connected={signaling.connected}
          connecting={signaling.connecting}
          roomId={activeRoomId}
          error={signaling.error}
          onLeave={handleLeaveRoom}
        />

        <div className="flex-1 min-h-0">
          <AppShell currentTab={currentTab} onTabChange={setCurrentTab}>
            {currentTab === 'receive' && (
              <ReceiveTab
                onRoomCreated={handleRoomCreated}
                isAuthenticated={isAuthenticated}
                onRequireLogin={() => setAuthOpen(true)}
              />
            )}
            {currentTab === 'send' && (
              <SendTab webrtc={webrtc} signaling={signaling.client} onJoinRoom={openJoinDialog} />
            )}
            {currentTab === 'chat' && (
              chatPeer ? (
                <ChatPage peerId={chatPeer.id} peerName={chatPeer.name} onBack={handleBackToFriends} />
              ) : (
                <FriendsPage onChatWith={handleChatWith} />
              )
            )}
            {currentTab === 'settings' && (
              <SettingsTab onOpenAuth={() => setAuthOpen(true)} />
            )}
          </AppShell>
        </div>

        {joinDialog.open && (
          <Suspense
            fallback={
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm">
                <div className="card p-6 text-sm text-muted">加载中…</div>
              </div>
            }
          >
            <JoinRoom
              initialCode={joinDialog.initialCode}
              onJoined={handleJoined}
              onClose={() => setJoinDialog({ open: false })}
            />
          </Suspense>
        )}

        <ToastContainer />

        {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}
      </div>
    </AuthGate>
  );
}

// 顶部连接状态条
function StatusBar({
  connected,
  connecting,
  roomId,
  error,
  onLeave,
}: {
  connected: boolean;
  connecting: boolean;
  roomId: string | null;
  error: string | null;
  onLeave: () => void;
}) {
  if (!roomId && !error) return null;
  const state = error ? 'error' : connecting ? 'connecting' : connected ? 'connected' : 'idle';
  const dot = state === 'connected' ? 'bg-success' : state === 'connecting' ? 'bg-warning' : state === 'error' ? 'bg-danger' : 'bg-muted';
  const text = error ?? (connecting ? '连接中…' : connected ? '已连接房间' : '未连接');

  return (
    <div className="flex items-center justify-between px-4 py-1.5 bg-surface border-b border-border/60 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2 h-2 rounded-full ${dot} ${state === 'connecting' ? 'animate-pulse' : ''}`} />
        <span className="text-muted truncate">{text}</span>
      </div>
      {roomId && connected && (
        <button type="button" onClick={onLeave} className="text-muted hover:text-danger shrink-0">
          离开
        </button>
      )}
    </div>
  );
}
